import axios from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";
import type { SimulateThreadMessage } from "@service/maya-simulator";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;
const BASE = `${apiURL}/api/v1/receptionist`;

/**
 * The receptionist half of the Maya Chat Simulator.
 *
 * The recruitment side has `POST /whatsapp/simulate` because staging points at
 * the production WhatsApp sender, so a real inbound cannot be relied on for
 * testing. The receptionist epic (ourteam-backend#4666, isolation contract
 * point 8) requires the same affordance on its own runtime — "without it none
 * of #4673–#4676 can be verified before launch" — but no endpoint exists yet.
 *
 * PROPOSED CONTRACT — ourteam-backend#4709. Field names mirror the recruitment
 * simulator one-for-one so the backend can lift the pattern rather than invent
 * a second shape; `merchant_id` is the one addition, and it is the only field
 * here that is not already in the codebase. Nothing is assumed about the
 * response beyond `replies`: everything else is optional and read defensively,
 * so a backend that answers a narrower payload still drives the screen.
 *
 *   POST /api/v1/receptionist/simulate
 *     { merchant_id, phone, body, profile_name?, media? }
 *     → { replies: string[], conversation_id?, booking_id?, flow?: { current_step, progress } }
 *
 * Until the endpoint ships every call 404s. `isSimulatorUnavailable` tells that
 * apart from a real failure so the screen can say "not on this environment yet"
 * instead of "something went wrong".
 */

/** Mirrors `SimulateMediaInput` on the recruitment simulator. */
export interface ReceptionistSimulateMedia {
    url: string;
    content_type: string;
}

export interface ReceptionistSimulateRequest {
    /** The merchant whose receptionist answers. Also carried as the `[RCP:<id>]`
     *  entry tag on the first message, which is how `dispatch.claims()` routes
     *  a customer while `RECEPTIONIST_SHARED_NUMBER` is on (#4666). */
    merchant_id: string;
    /** Test customer phone in E.164. */
    phone: string;
    body: string;
    profile_name?: string;
    media?: ReceptionistSimulateMedia[];
}

/** Where the customer is in the merchant's saved booking flow (#4674). */
export interface ReceptionistFlowState {
    current_step: string;
    progress: string;
}

/** One reply as it reached the customer: its text and, for a service's picture
 *  (#6681), the image it carried. */
export interface ReceptionistReplyItem {
    body: string;
    imageUrl?: string;
}

export interface ReceptionistSimulateResponse {
    replies: string[];
    /** `replies` with their images, one for one (ourteam-backend#6725). Empty when
     *  the backend predates it — read `replies` then. */
    replyItems: ReceptionistReplyItem[];
    /** The receptionist conversation this turn belongs to. */
    conversationId: string;
    /** Set once the flow has produced a booking request. */
    bookingId: string;
    flow?: ReceptionistFlowState;
}

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
}

/** True when the failure is "this backend has no receptionist simulator yet". */
export function isSimulatorUnavailable(err: unknown): boolean {
    const status = (err as { response?: { status?: number } })?.response?.status;
    return status === 404 || status === 501;
}

interface RawResponse {
    code?: number;
    message?: string;
    data?: RawResponse;
    replies?: string[];
    reply_items?: { body?: string; media?: { url?: string; contentType?: string }[] }[];
    conversation_id?: string;
    booking_id?: string;
    flow?: ReceptionistFlowState;
}

/** The first image among a message's attachments. A service picture is logged
 *  as `contentType: "image"`; an upload carries its real MIME type. */
export function firstImage(media?: { url?: string; contentType?: string }[] | null): string | undefined {
    if (!Array.isArray(media)) return undefined;
    const hit = media.find((m) => m?.url && (m.contentType ?? "").toLowerCase().startsWith("image"));
    return hit?.url || undefined;
}

/** Read the payload out of either the `{ code, message, data }` envelope or a
 *  bare body, the same way the recruitment simulator does. */
function readResponse(raw: RawResponse): ReceptionistSimulateResponse {
    if (typeof raw?.code === "number" && raw.code >= 400) {
        throw new Error(raw.message || "The receptionist simulator request failed.");
    }
    const data = raw?.data ?? raw;
    return {
        replies: Array.isArray(data.replies) ? data.replies : [],
        replyItems: Array.isArray(data.reply_items)
            ? data.reply_items.map((item) => ({
                  body: item?.body ?? "",
                  imageUrl: firstImage(item?.media),
              }))
            : [],
        conversationId: data.conversation_id ?? "",
        bookingId: data.booking_id ?? "",
        flow: data.flow,
    };
}

/** Drive one turn of the receptionist simulator. Admin-only, like its recruitment twin. */
export async function simulateReceptionistChat(
    payload: ReceptionistSimulateRequest
): Promise<ReceptionistSimulateResponse> {
    try {
        const res = await axios.post(`${BASE}/simulate`, payload, {
            headers: authHeaders(),
            // The turn runs synchronously on the backend (no Twilio), so allow room.
            timeout: 60000,
        });
        return readResponse(res.data as RawResponse);
    } catch (error) {
        if (!isSimulatorUnavailable(error)) {
            catchErrorLog(`[receptionist-sim] simulate failed: ${error}`);
        }
        throw error;
    }
}

export interface ReceptionistSimulateUploadRequest {
    merchant_id: string;
    phone: string;
    file: File;
    caption?: string;
}

/** Send an attachment — the receptionist accepts photos of a referral letter,
 *  a voice note, and so on (#4673). Multipart, mirroring `simulateMayaUpload`. */
export async function simulateReceptionistUpload(
    payload: ReceptionistSimulateUploadRequest
): Promise<ReceptionistSimulateResponse> {
    const token = getCookie("authToken");
    const form = new FormData();
    form.append("merchant_id", payload.merchant_id);
    form.append("phone", payload.phone);
    form.append("file", payload.file);
    if (payload.caption) form.append("caption", payload.caption);
    try {
        const res = await axios.post(`${BASE}/simulate/upload`, form, {
            // No Content-Type: axios sets the multipart boundary itself.
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            timeout: 120000,
        });
        return readResponse(res.data as RawResponse);
    } catch (error) {
        if (!isSimulatorUnavailable(error)) {
            catchErrorLog(`[receptionist-sim] upload failed: ${error}`);
        }
        throw error;
    }
}

/**
 * The authoritative thread for the simulated customer, so the messages the
 * runtime writes on its own — reminders, the staff decision relayed back
 * (#4674), a nudge after silence — appear in the panel. The synchronous
 * simulate reply cannot return them because they are sent later.
 *
 * GET /api/v1/receptionist/simulate/thread?merchant_id=&phone=&since=&limit=
 */
export async function getReceptionistSimulateThread(
    merchantId: string,
    phone: string,
    since?: string,
    limit = 200
): Promise<SimulateThreadMessage[]> {
    const res = await axios.get(`${BASE}/simulate/thread`, {
        headers: authHeaders(),
        params: { merchant_id: merchantId, phone, ...(since ? { since } : {}), limit },
    });
    const body = res.data as { data?: { messages?: SimulateThreadMessage[] } | SimulateThreadMessage[] };
    const data = body?.data ?? body;
    const rows = Array.isArray(data) ? data : (data as { messages?: SimulateThreadMessage[] })?.messages;
    return Array.isArray(rows) ? rows : [];
}
